import { useSWRConfig } from 'swr';
import { isCompletePet } from '@web/onboarding/OnboardingFlowContainer/guard';
import { useShopifyCartContext } from '@web/onboarding/OnboardingFlowContainer/ShopifyCartProvider';
import { EMPTY_PET } from '@web/pets';
import { buildUpdatedPetData } from './buildUpdatedPetData';

export const useShopifyPet = petId => {
  const { cart, addPetData, isLoading } = useShopifyCartContext();
  const { mutate: globalMutate, cache } = useSWRConfig();

  const getPet = id => (id && cart.pets?.find(pet => pet.id === id)) ?? null;
  const isCartLoading = isLoading;

  const getIncompleteCartPet = cart =>
    cart.pets?.find(pet => !isCompletePet(pet)) ||
    cart.pets?.find(pet => !pet.starter_box);
  const validPet = getPet(petId) ?? getIncompleteCartPet(cart) ?? EMPTY_PET;

  const savePet = async (values, options = {}) => {
    const existingPet = getPet(petId) ?? getIncompleteCartPet(cart);
    const updatedPetData = buildUpdatedPetData(existingPet, values);
    const currentCart = await addPetData(updatedPetData);

    const pet = currentCart
      ? currentCart.pets?.find(p => p.id === petId) ??
        currentCart.pets?.find(pet => !isCompletePet(pet))
      : cart?.pets?.[0];

    if (pet) {
      const planCombinationsUrlPrefix = `/api/v4/onboarding/${cart.cart_id}/pets/${petId}/plans/`;
      const cacheKeys = Array.from(cache.keys());
      const matchingKeys = cacheKeys.filter(
        key => typeof key === 'string' && key === planCombinationsUrlPrefix,
      );
      if (matchingKeys.length > 0) {
        // Only wait for mutations if explicitly requested (for now only allergy changes)
        if (options.waitForMealPlans) {
          await Promise.all(
            matchingKeys.map(key =>
              globalMutate(key, undefined, { revalidate: true }),
            ),
          );
        } else {
          // Fire and forget for non-allergy updates
          matchingKeys.forEach(key => globalMutate(key));
        }
      }
    }

    return {
      status: 200,
      data: { pet, pets: currentCart.pets, cart: currentCart },
    };
  };

  return {
    pet: validPet,
    isLoading: isCartLoading,
    savePet,
  };
};
